import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  Component,
  inject,
  ViewChild,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogClose,
  MatDialogActions,
  MatDialogTitle,
  MatDialogContent,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from '../shared.module';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { PopupServiceService } from '../../core/service/popup/popup-service.service';
import { LoaderService } from '../../core/service/loader.service';
import { AuthenticationService } from '../../core/service/auth/authentication.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface PeriodicElement {
  name: string;
  role: string;
  action: string;
}

@Component({
  selector: 'app-delegate-user-access',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HttpClientModule,
    SharedModule,
    MatDialogClose,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
  ],
  templateUrl: './delegate-user-access.component.html',
  styleUrl: './delegate-user-access.component.scss',
})
export class DelegateUserAccessComponent implements OnInit, AfterViewInit {
  private readonly data = inject(MAT_DIALOG_DATA);
  private _liveAnnouncer = inject(LiveAnnouncer);
  readonly popup = inject(PopupServiceService);
  public loader = inject(LoaderService);
  auth = inject(AuthenticationService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  searchControl = new FormControl('');

  displayedColumns: string[] = ['name', 'role', 'action'];
  dataSource = new MatTableDataSource<PeriodicElement>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngOnInit(): void {
    this.dataSource.data = this.data ?? [];
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const fullName = `${data?.userDetails?.firstName ?? ''} ${
        data?.userDetails?.lastName ?? ''
      }`.toLowerCase();
      return fullName.includes(filter);
    };

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: string | null) => {
        const filter = (searchTerm || '').trim().toLowerCase();
        this.dataSource.filter = filter;

        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  async switchUser(element: any) {
    this.loader.show();
    const currentUserData = await this.localStorageService.getKeyIndexedDbKey(
      'currentUser'
    );
    const payload = {
      switchUserId: element?.id,
      userId: currentUserData?.id,
    };
    this.auth.switchLogin(payload).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.loader.hide();
          this.localStorageService.setKey('switchUserToken', res.data.token);
          this.localStorageService.setIndexedDbKey('switchUser', res.data);
          this.localStorageService.setKey('isSwitchUser', JSON.stringify(true));
          window.location.href = '/dashboard/main-dashboard';
        } else {
          this.loader.hide();
          this.popup.successAndErrorDialog('error', res?.message);
        }
      },
      error: (error: any) => {
        this.loader.hide();
        this.popup.successAndErrorDialog('error', error?.error.message);
      },
    });
  }
}
